import { useMemo } from 'react';
import type {
  TenantSelectionResult,
  TenantSelectorRowData,
} from '../../../components/tenant-selector/types';
import { useTenantSelectorRowData } from '../../../components/tenant-selector/useTenantSelectorRowData';
import type { TenantSelectionConflict } from '../engage-automations-tab/templates.types';

export function useSegmentTenantSelection(options: {
  protectedTenantIds: string[];
  conflictingAutomationNamesByBusinessId: Record<string, string[]>;
}): TenantSelectionResult {
  const { protectedTenantIds, conflictingAutomationNamesByBusinessId } = options;

  const rowDataByTenantId = useMemo(() => {
    const map: Record<string, Partial<TenantSelectorRowData>> = {};
    for (const id of protectedTenantIds) {
      const names = conflictingAutomationNamesByBusinessId[id];
      const tooltip =
        Array.isArray(names) && names.length > 0
          ? `This tenant has automations that use this segment: ${names.join(', ')}. Remove the segment from those automations (or disable those automations for the tenant) before you can remove them from the selection.`
          : 'This tenant has an automation that uses this segment. Remove the segment from that automation (or disable that automation for the tenant) before you can remove them from the selection.';
      map[id] = {
        statusIndicator: {
          label: 'Used by automation',
          indicator: 'warning',
          lockReason: tooltip,
        },
      };
    }
    return map;
  }, [protectedTenantIds, conflictingAutomationNamesByBusinessId]);

  const { tenantRowData, isLoading } = useTenantSelectorRowData(rowDataByTenantId);

  const conflictItems: TenantSelectionConflict[] = protectedTenantIds.map(id => {
    const names = conflictingAutomationNamesByBusinessId[id];
    return {
      tenantId: id,
      tenantName: tenantRowData[id]?.name ?? id,
      description:
        Array.isArray(names) && names.length > 0
          ? `used by: ${names.join(', ')}`
          : 'used by an active automation',
    };
  });

  return {
    tenantRowData,
    isLoading,
    conflicts:
      conflictItems.length > 0
        ? {
            notice: {
              title: 'Protected tenants',
              description:
                'Some tenants cannot be removed because they have active automations using this segment.',
            },
            items: conflictItems,
          }
        : null,
    isValidating: false,
    syncingTenantCount: 0,
    jobSyncActive: false,
  };
}
